import React, { useState, useEffect } from "react";
import Navbar from "../shared/Navbar";
import { useUser } from "../components/UserContext";
import LoginModalComponent from "..//components/login/ModalLoginComponent";
import ChangePasswordModal from "../components/login/ChangePasswordModal";
import { Button } from "react-bootstrap";
import Footer from "../shared/Footer";
import { API_URL, SERVER_URL } from "../shared/apiConfig";
import "./Login.css";

function Login() {
    const { user, logout } = useUser();
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [createdDrinks, setCreatedDrinks] = useState([]);
    const [loadingDrinks, setLoadingDrinks] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
    const [searchQuery, setSearchQuery] = useState("");
    const itemsPerPage = 4; // Number of drinks per page

    const handleSearchInputChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    // Fetch user's created drinks
    useEffect(() => {
        const fetchCreatedDrinks = async () => {
            if (!user) return;
            setLoadingDrinks(true);
            try {
                const token = localStorage.getItem("token");

                const response = await fetch(`${API_URL}/drinkapi/user-drinks`, {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    },
                });

                if (response.ok) {
                    const drinks = await response.json();
                    setCreatedDrinks(drinks);
                } else {
                    throw new Error("Failed to fetch created drinks.");
                }
            } catch (err) {
                console.error("Error fetching created drinks:", err);
                setError(err.message);
            } finally {
                setLoadingDrinks(false);
            }
        };

        fetchCreatedDrinks();
    }, [user]);

    const filteredDrinks = createdDrinks.filter((drink) =>
        drink.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        drink.ingredientDTOs.some(ingredient =>
            ingredient.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const sortedDrinks = [...filteredDrinks].sort((a, b) => {
        // Handle special case for ingredients which is an array
        if (sortConfig.key === 'ingredients') {
            const aIngredients = a.ingredientDTOs.map(i => i.name).join(', ');
            const bIngredients = b.ingredientDTOs.map(i => i.name).join(', ');
            if (aIngredients < bIngredients) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aIngredients > bIngredients) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        }

        // For regular properties
        if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentDrinks = sortedDrinks.slice(indexOfFirstItem, indexOfLastItem);

    // Calculate total pages
    const totalPages = Math.ceil(createdDrinks.length / itemsPerPage);

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const requestSort = (key) => {
        let direction = "ascending";
        if (sortConfig.key === key && sortConfig.direction === "ascending") {
            direction = "descending";
        }
        setSortConfig({ key, direction });
    };

    // Handle account deletion
    const handleDeleteAccount = async () => {
        const confirmDelete = window.confirm("Are you sure you want to delete your account? This action cannot be undone.");
        if (!confirmDelete) return;

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/auth/delete-account`, {
                method: "DELETE",
                headers: { "Authorization": `Bearer ${token}` },
            });

            if (response.ok) {
                alert("Account deleted successfully.");
                localStorage.removeItem("token"); // Remove JWT
                await logout(); // Log out the user
            } else {
                alert("Failed to delete account.");
            }
        } catch (error) {
            console.error("Error deleting account:", error);
            alert("An error occurred while deleting your account.");
        }
    };

    const handleLogout = async () => {
        const result = await logout();
        if (result.success) {
            alert("Logged out successfully!");
        } else {
            alert("Logout failed.");
        }
    };

    return (
        <div className="page-container">
            <Navbar />
            <div className="login-content">
                {!user ? (
                    <div className="login-welcome">
                        <h1>Velkommen til Matcha & Mocha</h1>
                        <p>Vennligst logg inn eller registrer deg for å lagre dine favorittdrikker og skreddersy din Drømmekoppen-opplevelse.</p>
                        <LoginModalComponent />
                    </div>
                ) : (
                    <div className="login-profile">
                        {/* LEFT COLUMN: Profile Info */}
                        <div className="profile-info">
                            <h2>Velkommen, {user?.name || user?.username}!</h2>
                            <p>Administrer profilinnstillingene dine her.</p>
                            <Button
                                variant="primary"
                                onClick={() => setShowPasswordModal(true)}
                                className="profile-button"
                            >
                                Bytt passord
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDeleteAccount}
                                className="profile-button"
                            >
                                Slett konto
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={handleLogout}
                                className="profile-button"
                            >
                                Logg ut
                            </Button>
                        </div>

                        {/* RIGHT COLUMN: DreamCups */}
                        <div className="dreamcups-info">
                            <h3>Dine Drømmekopper</h3>

                            <input
                                type="text"
                                placeholder="Søk etter drømmekopper..."
                                value={searchQuery}
                                onChange={handleSearchInputChange}
                                className="search-input"
                            />
                            {loadingDrinks ? (
                                <p>Laster inn dine Drømmekopper...</p>
                            ) : error ? (
                                <p className="error">Error: {error}</p>
                            ) : filteredDrinks.length > 0 ? (
                                <>
                                    <table className="drinks-table">
                                        <thead>
                                            <tr>
                                                <th>Bilde</th>
                                                <th onClick={() => requestSort('name')}>
                                                    Navn {sortConfig.key === 'name' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
                                                </th>
                                                <th onClick={() => requestSort('ingredients')}>
                                                    Ingredienser {sortConfig.key === 'ingredients' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
                                                </th>
                                                <th onClick={() => requestSort('timesFavorite')}>
                                                    Likes {sortConfig.key === 'timesFavorite' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentDrinks.map((drink) => {
                                                const normalizedPath = drink.imagePath?.replace(/\\/g, '/');
                                                const imageSrc = normalizedPath
                                                    ? `${SERVER_URL}/${normalizedPath}`
                                                    : `${process.env.PUBLIC_URL}/images/default.png`;

                                                return (
                                                    <tr key={drink.drinkId} className="drink-row">
                                                        <td>
                                                            <img
                                                                src={imageSrc}
                                                                alt={drink.name}
                                                                className="drink-image"
                                                                onError={(e) => {
                                                                    if (!e.target.dataset.fallback) {
                                                                        console.warn(`Fallback triggered for ${drink.name}`);
                                                                        e.target.src = `${process.env.PUBLIC_URL}/images/default.png`;
                                                                        e.target.dataset.fallback = "true";
                                                                    }
                                                                }}
                                                            />
                                                        </td>
                                                        <td>{drink.name}</td>
                                                        <td>{drink.ingredientDTOs.map((ingredient) => ingredient.name).join(', ')}</td>
                                                        <td>{drink.timesFavorite}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="pagination">
                                            {Array.from({ length: totalPages }, (_, index) => (
                                                <button
                                                    key={index + 1}
                                                    onClick={() => handlePageChange(index + 1)}
                                                    className={currentPage === index + 1 ? 'active' : ''}
                                                >
                                                    {index + 1}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p>Du har ikke opprettet noen drømmekopper ennå</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <Footer />
            <ChangePasswordModal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} />
        </div>
    );
}

export default Login;